import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { 
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  PlayArrow as ProcessIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

/** Prefer ISIN day_basis (364/365); fall back to 364 to match buyback convention. */
const resolveDayCountBasis = (dayBasis) => {
  const n = parseInt(dayBasis, 10);
  return n === 365 || n === 364 ? n : 364;
};

const PrematureMaturityPage = () => {
  // Premature maturity is an attributable action - send the acting user so the
  // backend records who did it instead of falling back to a placeholder account.
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user')) || null;
    } catch {
      return null;
    }
  })();

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [productType, setProductType] = useState('all');
  const [prematureMaturityDate, setPrematureMaturityDate] = useState(new Date());
  const [systemDate, setSystemDate] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showBuybackDialog, setShowBuybackDialog] = useState(false);
  const [buybackEdits, setBuybackEdits] = useState([]);
  const [summary, setSummary] = useState({
    totalDeals: 0,
    totalFaceValue: 0,
    selectedCount: 0,
    selectedFaceValue: 0
  });

  useEffect(() => {
    fetchSystemDate();
  }, []);

  useEffect(() => {
    fetchDeals();
  }, [productType]);

  useEffect(() => {
    calculateSummary();
  }, [deals, selectedDeals]);

  const getDealRowKey = (deal) => `${deal?.product_type || 'unknown'}:${deal?.id}`;

  const formatDateForApi = (dateValue) => {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const fetchSystemDate = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/system-day`);
      const day = response?.data?.system_day;
      if (day) {
        const dayStr = String(day).slice(0, 10);
        const parsed = new Date(`${dayStr}T00:00:00`);
        if (!isNaN(parsed.getTime())) {
          setSystemDate(parsed);
          setPrematureMaturityDate(parsed);
        }
      }
    } catch (err) {
      // Fallback to local date if system day API is not reachable
      console.warn('Failed to fetch system day for premature maturity page:', err?.message || err);
      setSystemDate(new Date());
    }
  };

  const fetchDeals = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const params = new URLSearchParams({
        productType: productType
      });
      
      const response = await axios.get(`${API_BASE_URL}/maturity/premature?${params}`);
      
      if (response.data.success) {
        setDeals(response.data.data || []);
        setSelectedDeals([]); // Reset selection when fetching new data
      } else {
        setError(response.data.error || 'Failed to fetch deals');
      }
    } catch (err) {
      setError('Error fetching deals: ' + (err.response?.data?.error || err.message));
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = () => {
    const totalFaceValue = deals.reduce((sum, deal) => sum + (parseFloat(deal.face_value) || 0), 0);
    const selectedFaceValue = selectedDeals.reduce((sum, dealKey) => {
      const deal = deals.find(d => getDealRowKey(d) === dealKey);
      return sum + (deal ? (parseFloat(deal.face_value) || 0) : 0);
    }, 0);

    setSummary({
      totalDeals: deals.length,
      totalFaceValue,
      selectedCount: selectedDeals.length,
      selectedFaceValue
    });
  };

  const handleSelectDeal = (dealKey) => {
    setSelectedDeals(prev => {
      if (prev.includes(dealKey)) {
        return prev.filter(id => id !== dealKey);
      } else {
        return [...prev, dealKey];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedDeals.length === deals.length) {
      setSelectedDeals([]);
    } else {
      setSelectedDeals(deals.map(deal => getDealRowKey(deal)));
    }
  };

  const handleProcessPrematureMaturity = () => {
    if (selectedDeals.length === 0) {
      setError('Please select at least one deal to mature prematurely');
      return;
    }

    if (!prematureMaturityDate) {
      setError('Please select a premature maturity date');
      return;
    }

    // Validate that all selected deals are of the same product type
    const selectedDealTypes = new Set(
      selectedDeals.map(dealKey => {
        const deal = deals.find(d => getDealRowKey(d) === dealKey);
        return deal?.product_type;
      })
    );

    if (selectedDealTypes.size > 1) {
      setError('All selected deals must be of the same product type. Please select deals from one product type only.');
      return;
    }

    const dealType = Array.from(selectedDealTypes)[0];
    if (!dealType) {
      setError('Unable to determine product type for selected deals');
      return;
    }

    if (dealType === 'buyback') {
      const dateStr = formatDateForApi(prematureMaturityDate);
      const edits = selectedDeals.map(dealKey => {
        const d = deals.find(x => getDealRowKey(x) === dealKey) || {};
        return {
          dealKey,
          dealId: d.id,
          deal_number: d.deal_number,
          isin: d.isin,
          leg1_face_value: Number(d.leg1_face_value) || Number(d.face_value) || 0,
          leg1_value_date: d.leg1_value_date,
          leg1_settlement_amount: Number(d.leg1_settlement_amount) || 0,
          leg1InterestRate: d.leg1_interest_rate != null ? String(d.leg1_interest_rate) : '',
          leg2_accrued_interest: d.leg2_accrued_interest != null ? Number(d.leg2_accrued_interest) : null,
          coupon_rate: d.coupon_rate,
          issue_date: d.issue_date,
          coupon_date1: d.coupon_date1,
          coupon_date2: d.coupon_date2,
          leg2ValueDate: dateStr,
          dayCountBasis: resolveDayCountBasis(d.day_basis)
        };
      });
      setBuybackEdits(edits);
      setShowBuybackDialog(true);
      return;
    }

    setShowConfirmDialog(true);
  };

  const calcDaysBetween = (d1, d2) => {
    if (!d1 || !d2) return 0;
    const a = new Date(d1);
    const b = new Date(d2);
    if (isNaN(a.getTime()) || isNaN(b.getTime())) return 0;
    const diff = Math.abs(b - a);
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const computeBuybackPreview = (row) => {
    const round4 = (n) => Math.round(n * 10000) / 10000;
    const principal = Number(row.leg1_settlement_amount) || 0;
    const face = Number(row.leg1_face_value) || 0;
    const rate = parseFloat(row.leg1InterestRate);
    const basis = parseInt(row.dayCountBasis, 10) || 364;
    const days = calcDaysBetween(row.leg1_value_date, row.leg2ValueDate);
    if (!isFinite(rate)) {
      return {
        days,
        interest: 0,
        accruedInterestPer100: row.leg2_accrued_interest || 0,
        dirtyPricePer100: 0,
        cleanPricePer100: 0,
        newLeg2Settlement: principal
      };
    }
    const interest = principal * (rate / 100) * (days / basis);
    const newLeg2Settlement = Math.round((principal + interest) * 100) / 100;
    const dirtyPricePer100 = face > 0 ? round4((newLeg2Settlement / face) * 100) : 0;

    // Keep accrued-interest logic aligned with backend recalc; fallback to existing leg2 accrued value when metadata is incomplete.
    const calcAccruedPer100 = () => {
      if (!row.coupon_rate || !row.issue_date || !row.leg2ValueDate || !row.coupon_date1 || !row.coupon_date2) {
        return row.leg2_accrued_interest != null ? Number(row.leg2_accrued_interest) : 0;
      }

      const cr = parseFloat(row.coupon_rate) / 100;
      const settle = new Date(row.leg2ValueDate);
      const issue = new Date(row.issue_date);
      if (!isFinite(cr) || isNaN(settle.getTime()) || isNaN(issue.getTime())) {
        return row.leg2_accrued_interest != null ? Number(row.leg2_accrued_interest) : 0;
      }

      const frequency = 2;
      const couponPer100 = (100 * cr) / frequency;
      const monthsPerPeriod = 12 / frequency;

      let lastCoupon = new Date(issue);
      while (lastCoupon <= settle) {
        lastCoupon.setMonth(lastCoupon.getMonth() + monthsPerPeriod);
      }
      lastCoupon.setMonth(lastCoupon.getMonth() - monthsPerPeriod);

      const nextCoupon = new Date(lastCoupon);
      nextCoupon.setMonth(nextCoupon.getMonth() + monthsPerPeriod);

      const daysInPeriod = calcDaysBetween(nextCoupon, lastCoupon);
      const daysAccrued = calcDaysBetween(settle, lastCoupon);
      if (!daysInPeriod) return row.leg2_accrued_interest != null ? Number(row.leg2_accrued_interest) : 0;

      return round4(couponPer100 * (daysAccrued / daysInPeriod));
    };

    const accruedInterestPer100 = round4(calcAccruedPer100());
    const cleanPricePer100 = round4(dirtyPricePer100 - accruedInterestPer100);
    return {
      days,
      interest: Math.round(interest * 100) / 100,
      accruedInterestPer100,
      dirtyPricePer100: round4(cleanPricePer100 + accruedInterestPer100),
      cleanPricePer100,
      newLeg2Settlement
    };
  };

  const updateBuybackEdit = (index, patch) => {
    setBuybackEdits(prev => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const confirmBuybackPrematureMaturity = async () => {
    const invalid = buybackEdits.find(row => {
      const r = parseFloat(row.leg1InterestRate);
      return !isFinite(r) || r < 0 || !row.leg2ValueDate;
    });
    if (invalid) {
      setError('Every buyback row needs a valid interest rate and Leg 2 value date');
      return;
    }
    const tooEarly = buybackEdits.find(row => row.leg2ValueDate < formatDateForApi(row.leg1_value_date));
    if (tooEarly) {
      setError(`${tooEarly.deal_number}: the premature date cannot be before the value date (${formatDateForApi(tooEarly.leg1_value_date)})`);
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        userId: currentUser?.id ?? null,
        deals: buybackEdits.map(row => ({
          dealId: row.dealId,
          leg1InterestRate: parseFloat(row.leg1InterestRate),
          leg2ValueDate: row.leg2ValueDate,
          dayCountBasis: parseInt(row.dayCountBasis, 10) || 364
        }))
      };

      const response = await axios.post(`${API_BASE_URL}/maturity/premature/buyback`, payload);

      if (response.data.success) {
        const resultRows = Array.isArray(response.data.results) ? response.data.results : [];
        const gsecSyncLines = resultRows.map(r => {
          if (r.old_gsec_deal_number && r.new_gsec_deal_number) {
            return `${r.deal_number}: GSEC replaced -> ${r.old_gsec_deal_number} -> ${r.new_gsec_deal_number}`;
          }
          if (!r.old_gsec_deal_number && r.new_gsec_deal_number) {
            return `${r.deal_number}: GSEC created -> ${r.new_gsec_deal_number}`;
          }
          const nums = Array.isArray(r.gsec_updated_deal_numbers) ? r.gsec_updated_deal_numbers : [];
          return nums.length > 0
            ? `${r.deal_number}: GSEC updated -> ${nums.join(', ')}`
            : `${r.deal_number}: GSEC sync -> no linked buy deal found (buyback saved)`;
        });
        const baseMsg = response.data.message || `Recalculated ${response.data.updatedCount} buyback deal(s)`;
        setSuccess(gsecSyncLines.length > 0 ? `${baseMsg}\n${gsecSyncLines.join('\n')}` : baseMsg);
        if (Array.isArray(response.data.errors) && response.data.errors.length > 0) {
          setError(`Some selected deals were blocked:\n${response.data.errors.join('\n')}`);
        }
        setShowBuybackDialog(false);
        setBuybackEdits([]);
        setSelectedDeals([]);
        await fetchDeals();
      } else {
        setError(response.data.error || 'Failed to process buyback premature maturity');
      }
    } catch (err) {
      const apiError = err.response?.data?.error || err.message;
      const details = Array.isArray(err.response?.data?.errors) ? err.response.data.errors.join('\n') : '';
      setError(`Error processing buyback premature maturity: ${apiError}${details ? `\n${details}` : ''}`);
    } finally {
      setProcessing(false);
    }
  };

  const confirmProcessPrematureMaturity = async () => {
    setShowConfirmDialog(false);
    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const dateStr = formatDateForApi(prematureMaturityDate);
      const selectedRows = deals.filter(d => selectedDeals.includes(getDealRowKey(d)));
      const dealType = selectedRows[0]?.product_type;
      const dealIds = selectedRows.map(d => d.id);

      const response = await axios.post(`${API_BASE_URL}/maturity/premature`, {
        dealIds,
        prematureMaturityDate: dateStr,
        productType: dealType,
        userId: currentUser?.id ?? null
      });

      if (response.data.success) {
        setSuccess(response.data.message || `Successfully matured ${response.data.updatedCount} deal(s) prematurely`);
        setSelectedDeals([]);
        // Refresh the deals list
        await fetchDeals();
      } else {
        setError(response.data.error || 'Failed to process premature maturity');
      }
    } catch (err) {
      setError('Error processing premature maturity: ' + (err.response?.data?.error || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    const n = Number(amount);
    if (!isFinite(n)) return '0.00';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  const formatPrice = (value) => {
    const n = Number(value);
    if (!isFinite(n)) return '0.0000';
    return n.toFixed(4);
  };

  const getProductTypeLabel = (type) => {
    const labels = {
      'gsec': 'GSEC',
      'money_market': 'Money Market',
      'repo': 'Repo',
      'buyback': 'Buyback'
    };
    return labels[type] || type;
  };

  const getDaysToMaturityColor = (days) => {
    if (days <= 7) return 'error';
    if (days <= 30) return 'warning';
    return 'default';
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Premature Maturity Management
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Select deals to mature early. Once matured, deals will appear in the Maturity Handling blotter on the selected date.
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">{summary.totalDeals}</Typography>
              <Typography variant="body2" color="textSecondary">Total Available Deals</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">{formatCurrency(summary.totalFaceValue)}</Typography>
              <Typography variant="body2" color="textSecondary">Total Face Value</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="secondary">{summary.selectedCount}</Typography>
              <Typography variant="body2" color="textSecondary">Selected Deals</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="secondary">{formatCurrency(summary.selectedFaceValue)}</Typography>
              <Typography variant="body2" color="textSecondary">Selected Face Value</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel>Product Type</InputLabel>
              <Select
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                label="Product Type"
              >
                <MenuItem value="all">All Products</MenuItem>
                <MenuItem value="gsec">GSEC</MenuItem>
                <MenuItem value="money_market">Money Market</MenuItem>
                <MenuItem value="repo">Repo</MenuItem>
                <MenuItem value="buyback">Buyback</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Premature Maturity Date"
                value={prematureMaturityDate}
                onChange={setPrematureMaturityDate}
                minDate={systemDate || new Date()}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={4} md={6}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchDeals}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ProcessIcon />}
                onClick={handleProcessPrematureMaturity}
                disabled={processing || selectedDeals.length === 0 || !prematureMaturityDate}
              >
                Mature Selected Deals
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2, whiteSpace: 'pre-line' }}
          onClose={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      {/* Deals Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedDeals.length > 0 && selectedDeals.length < deals.length}
                  checked={deals.length > 0 && selectedDeals.length === deals.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Product Type</TableCell>
              <TableCell>Deal Number</TableCell>
              <TableCell>ISIN</TableCell>
              <TableCell>Counterparty</TableCell>
              <TableCell align="right">Face Value</TableCell>
              <TableCell>Current Maturity Date</TableCell>
              <TableCell>Days to Maturity</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : deals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No deals available for premature maturity
                </TableCell>
              </TableRow>
            ) : (
              deals.map((deal) => {
                const rowKey = getDealRowKey(deal);
                return (
                  <TableRow 
                    key={rowKey} 
                    hover
                    selected={selectedDeals.includes(rowKey)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedDeals.includes(rowKey)}
                        onChange={() => handleSelectDeal(rowKey)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={getProductTypeLabel(deal.product_type)} size="small" />
                    </TableCell>
                    <TableCell>{deal.deal_number}</TableCell>
                    <TableCell>{deal.isin || 'N/A'}</TableCell>
                    <TableCell>{deal.counterparty_name || deal.counterparty || 'N/A'}</TableCell>
                    <TableCell align="right">{formatCurrency(deal.face_value)}</TableCell>
                    <TableCell>{formatDate(deal.maturity_date)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={`${deal.days_to_maturity} days`}
                        color={getDaysToMaturityColor(deal.days_to_maturity)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={deal.status || 'N/A'}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Premature Maturity
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to mature {selectedDeals.length} deal(s) prematurely.
          </Alert>
          <Typography variant="body2" gutterBottom>
            <strong>Premature Maturity Date:</strong> {formatDate(formatDateForApi(prematureMaturityDate))}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Selected Deals:</strong> {selectedDeals.length}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Total Face Value:</strong> {formatCurrency(summary.selectedFaceValue)}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="textSecondary">
            Once confirmed, the maturity date will be updated and these deals will appear in the Maturity Handling blotter on the selected date.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button 
            onClick={confirmProcessPrematureMaturity} 
            variant="contained" 
            color="primary"
            disabled={processing}
          >
            {processing ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Buyback Premature Maturity Dialog */}
      <Dialog
        open={showBuybackDialog}
        onClose={() => !processing && setShowBuybackDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Recalculate Buyback Deals for Premature Maturity</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Adjust Leg 1 Interest Rate and Leg 2 Value Date per deal. The new Leg 2 Settlement Amount is calculated as
            &nbsp;<strong>Leg 1 Settlement × Rate% × (Days / Basis)</strong>&nbsp; and added to Leg 1 Settlement.
          </Alert>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Deal Number</TableCell>
                  <TableCell align="right">Face Value</TableCell>
                  <TableCell>ISIN</TableCell>
                  <TableCell>Leg 1 Value Date</TableCell>
                  <TableCell align="right">Leg 1 Settlement</TableCell>
                  <TableCell>Leg 1 Interest Rate (%)</TableCell>
                  <TableCell>Leg 2 Value Date</TableCell>
                  <TableCell>Day Count</TableCell>
                  <TableCell align="right">Days</TableCell>
                  <TableCell align="right">Interest</TableCell>
                  <TableCell align="right">Leg 2 Accrued</TableCell>
                  <TableCell align="right">Leg 2 Dirty</TableCell>
                  <TableCell align="right">Leg 2 Clean</TableCell>
                  <TableCell align="right">New Leg 2 Settlement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {buybackEdits.map((row, idx) => {
                  const preview = computeBuybackPreview(row);
                  return (
                    <TableRow key={row.dealId}>
                      <TableCell>{row.deal_number}</TableCell>
                      <TableCell align="right">{formatCurrency(row.leg1_face_value || 0)}</TableCell>
                      <TableCell>{row.isin || 'N/A'}</TableCell>
                      <TableCell>{formatDate(row.leg1_value_date)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.leg1_settlement_amount)}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ step: '0.0001', min: 0 }}
                          value={row.leg1InterestRate}
                          onChange={(e) => updateBuybackEdit(idx, { leg1InterestRate: e.target.value })}
                          disabled={processing}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="date"
                          value={row.leg2ValueDate}
                          inputProps={{ min: formatDateForApi(row.leg1_value_date) || undefined }}
                          onChange={(e) => updateBuybackEdit(idx, { leg2ValueDate: e.target.value })}
                          disabled={processing}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={row.dayCountBasis}
                          onChange={(e) => updateBuybackEdit(idx, { dayCountBasis: Number(e.target.value) })}
                          disabled={processing}
                        >
                          <MenuItem value={364}>364</MenuItem>
                          <MenuItem value={365}>365</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell align="right">{preview.days}</TableCell>
                      <TableCell align="right">{formatCurrency(preview.interest)}</TableCell>
                      <TableCell align="right">{formatPrice(preview.accruedInterestPer100)}</TableCell>
                      <TableCell align="right">{formatPrice(preview.dirtyPricePer100)}</TableCell>
                      <TableCell align="right">{formatPrice(preview.cleanPricePer100)}</TableCell>
                      <TableCell align="right"><strong>{formatCurrency(preview.newLeg2Settlement)}</strong></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBuybackDialog(false)} disabled={processing}>Cancel</Button>
          <Button
            onClick={confirmBuybackPrematureMaturity}
            variant="contained"
            color="primary"
            disabled={processing || buybackEdits.length === 0}
          >
            {processing ? <CircularProgress size={20} /> : 'Save & Mature'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrematureMaturityPage;

